import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Settings2, Users, RotateCcw, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export const SMBAssignmentRules = () => {
  const [config, setConfig] = useState({ enabled: false, assignees: [], last_index: -1 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, usersRes] = await Promise.all([
        api.get('/smb/assignment-config'),
        api.get('/smb/users'),
      ]);
      if (configRes.data.success) setConfig(configRes.data.data);
      if (usersRes.data.success) setUsers(usersRes.data.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleAssignee = (userId) => {
    setConfig(prev => ({
      ...prev,
      assignees: prev.assignees.includes(userId)
        ? prev.assignees.filter(id => id !== userId)
        : [...prev.assignees, userId],
    }));
  };

  const handleSave = async () => {
    if (config.enabled && config.assignees.length === 0) {
      toast.error('Please select at least one team member');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/smb/assignment-config', config);
      if (res.data.success) {
        toast.success(res.data.message);
        setConfig(res.data.data);
      }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleReset = () => {
    setConfig(prev => ({ ...prev, last_index: -1 }));
    toast.info('Round-robin counter will reset on save');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  // Build next assignee preview
  const nextIdx = config.assignees.length > 0 ? (config.last_index + 1) % config.assignees.length : -1;
  const nextUser = nextIdx >= 0 ? users.find(u => u.id === config.assignees[nextIdx]) : null;

  return (
    <div className="space-y-6" data-testid="smb-assignment-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Assignment Rules</h1>
          <p className="text-muted-foreground">Configure automatic round-robin lead assignment for SMB leads</p>
        </div>
        <Button data-testid="save-assignment-config" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
          {saving ? 'Saving...' : 'Save Rules'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Settings2 className="h-4 w-4" />Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="rr-enabled">Auto-Assignment</Label>
                <p className="text-xs text-muted-foreground mt-0.5">New leads without an assignee will be auto-assigned</p>
              </div>
              <Switch id="rr-enabled" data-testid="assignment-enabled-toggle" checked={config.enabled} onCheckedChange={v => setConfig({ ...config, enabled: v })} />
            </div>

            <div className="pt-2 border-t">
              <p className="text-sm font-medium mb-1">Status</p>
              {config.enabled ? (
                <Badge className="bg-green-500/15 text-green-600 border-green-500/30">Active - {config.assignees.length} members in rotation</Badge>
              ) : (
                <Badge variant="outline">Disabled</Badge>
              )}
            </div>

            {nextUser && config.enabled && (
              <div className="pt-2 border-t">
                <p className="text-sm font-medium mb-1">Next Assignee</p>
                <p className="text-sm text-muted-foreground">{nextUser.first_name} {nextUser.last_name}</p>
              </div>
            )}

            <div className="pt-2 border-t">
              <Button variant="outline" size="sm" onClick={handleReset} className="w-full">
                <RotateCcw className="mr-2 h-3.5 w-3.5" />Reset Rotation Counter
              </Button>
              <p className="text-xs text-muted-foreground mt-1.5 text-center">Resets the round-robin back to the first member</p>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />Team Members ({config.assignees.length} selected)</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">Select team members to include in the round-robin assignment rotation.</p>
            <div className="space-y-2">
              {users.map(u => {
                const isSelected = config.assignees.includes(u.id);
                const rotationIdx = config.assignees.indexOf(u.id);
                return (
                  <div key={u.id} data-testid={`user-checkbox-${u.id}`}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50'}`}
                    onClick={() => toggleAssignee(u.id)}>
                    <div className="flex items-center gap-3">
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleAssignee(u.id)} />
                      <div>
                        <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    {isSelected && (
                      <Badge variant="outline" className="text-xs">#{rotationIdx + 1} in rotation</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
